import styled from "styled-components";

import { Table } from "@similarweb/pro-logic-components";
import type { SubtitleFilter } from "@similarweb/pro-logic-components";
import { IconButton } from "@similarweb/ui-components";

import type { IFlattenedCategory } from "app/common/services/categoryService.types";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import { numberFilter } from "app/filters/numberFilter";
import { EDataType } from "app/pages/digital-marketing/ad-intelligence/advertisers/overview/components/types";
import { IndustryDataAvailabilityBanner } from "app/pages/digital-marketing/ad-intelligence/common/data-availability/IndustryDataAvailabilityBanner";
import { allTrackers } from "app/services/track/track";
import { displayAdvertisingNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingNavigator";
import { publisherAnalysisNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/publisherAnalysis/publisherAnalysisNavigator";

import { TABLE_ID as TOP_ADVERTISERS_PUBLISHERS_TABLE_ID } from "../../../advertisersPublishers/TopAdvertisersPublishers";
import { useDisplayAdvertiserPublisherDataAndParams } from "../../../hooks/useDisplayAdvertiserPublisherDataAndParams";
import { useDurationSubtitleFilter } from "../../../hooks/useDurationSubtitleFilter";
import { WidgetBoxWithMarginBottom } from "../../../StyledComponents";
import { getNoDataObject, PAGE_SIZE_SMALL } from "../../../utils/utils";
import { TableFooter } from "../../StyledComponents";

import { getColumns } from "./IndustryTopPublishersColumns";
import { useIndustryTopPublishersPreview } from "./useIndustryTopPublishersPreview";

// Parent Stack already provides the widget-to-widget gap.
const TopPublishersWidgetBox = styled(WidgetBoxWithMarginBottom)`
    margin-bottom: 0;
`;

interface IIndustryTopPublishersProps {
    category: IFlattenedCategory;
}

export const IndustryTopPublishers = ({ category }: IIndustryTopPublishersProps) => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const { rows, totalCount, isLoading, isError, availability } = useIndustryTopPublishersPreview(category);

    const durationSubtitleFilter = useDurationSubtitleFilter();
    const subtitleFilters = useSubtitleFilters("country", "webSource");
    const combinedSubtitleFilters: SubtitleFilter[] = [durationSubtitleFilter, ...subtitleFilters];

    const handlePublisherClick = (publisher: string) => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", `Industry_overview/Top_publishers/${publisher}`);
        void publisherAnalysisNavigation.overview.go({ ...params, key: publisher });
    };

    const handleSeeAll = () => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", "Advertiser_overview/Top_publishers/see_more");
        void displayAdvertisingNavigation.publishers.go(params);
    };

    return (
        <TopPublishersWidgetBox
            id={TOP_ADVERTISERS_PUBLISHERS_TABLE_ID}
            data-automation="industry-top-publishers-widget"
        >
            <Table
                theme="white"
                bordersConfig={{ body: false, header: false }}
                columnConfig={getColumns(handlePublisherClick)}
                data={rows}
                isLoading={isLoading}
                loaderRowCount={PAGE_SIZE_SMALL}
                noData={getNoDataObject(isError)}
            >
                <Table.TableTop
                    borderBottom
                    withoutRowBorders
                >
                    <Table.TableTop.Title
                        withFiltersLoader
                        title={translate("ad.intelligence.advertiser.top.publishers.table.title")}
                        tooltip={translate("ad.intelligence.advertiser.top.publishers.table.title.tooltip", {
                            sortBy: EDataType.SPEND,
                        })}
                        filters={combinedSubtitleFilters}
                    />
                    {/* The table owns its title row, so the availability notice goes in the
                        table-top slot to sit directly under the title. */}
                    {availability.isBeforeMinDate ? (
                        <Table.TableTop.Chart>
                            <IndustryDataAvailabilityBanner availability={availability} />
                        </Table.TableTop.Chart>
                    ) : null}
                </Table.TableTop>
            </Table>
            {totalCount > 0 ? (
                <TableFooter>
                    <IconButton
                        type="flat"
                        onClick={handleSeeAll}
                        iconName="arrow-right"
                        placement="right"
                        iconSize="xs"
                        data-automation="industry-top-publishers-see-all"
                    >
                        {translate("ad.intelligence.advertiser.publishers.table.see.all.button", {
                            totalCount: numberFilter()(totalCount),
                        })}
                    </IconButton>
                </TableFooter>
            ) : null}
        </TopPublishersWidgetBox>
    );
};
